import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { get, trim, uniqueId, capitalize } from 'lodash';
import axios from 'axios';
import {
	SdfAlert,
	SdfBadge,
	SdfBusyIndicator,
	SdfFocusPane,
	SdfIcon,
	SdfInput,
	SdfLink,
	SdfTextarea,
	SdfTooltip,
	SdfButton
} from '@synerg/react-components';
import { CustomToolTip } from 'shared-one-ux-components';

import { setLoading, showErrorModal, useApplicationState } from '../../utilities/applicationState';
import withContext from '../common/withContext';
import { descriptionLength } from '../common/util';
import Toast from '../common/toast';
import ProjectContext from './projectContext';
import './projectTitle.scss';

function ProjectTitle(props = {}) {
	const [, dispatch] = useApplicationState();
	const { projectDetail, setProjectDetail, status, legacy } = props;
	const {
		name, description, b2cConsumerAppID, b2bConsumerAppID, _id: projectID, type, associatedProject = {}
	} = projectDetail;

	const [editMode, setEditMode] = useState(false);
	const [busy, setBusy] = useState(false);
	const [projectName, setProjectName] = useState(name);
	const [projectDescription, setProjectDescription] = useState(description);
	const [applicationNameList, setApplicationNameList] = useState([]);
	const [numberOfEndLineCharacter, setNumberOfEndLineCharacter] = useState(0);
	const [projectDescriptionLength, setProjectDescriptionLength] = useState(0);
	const [saveDisabled, setSaveDisabled] = useState(true);
	const [toast, setToast] = useState(false);

	let projectNamePrime = '' + projectName;
	let projectDescriptionPrime = '' + projectDescription;

	const defaultValues = {
		projectName: '',
		projectDescription: ''
	};

	const { control, setValue, handleSubmit, reset } = useForm(defaultValues);

	const validatorsForName = {
		tooShort: (value) => trim(value).length >= 3 || 'App name too short',
		tooLong: (value) => trim(value).length <= 40 || 'App name too long',
		inValidCharacters: (value) =>
			!/[^A-Za-z0-9-. ]/.test(trim(value)) ||
			'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces',
		nameTaken: (value) => {
			const pnt = trim(value);
			return (!applicationNameList.some((name) =>
                name.replace(/(_DEV|_delete_DEV)? - (Application|Data connector)/gi, '').toLowerCase()
                === pnt.replace(/(_DEV|_delete_DEV)? - (Application|Data connector)/gi, '').toLowerCase()
            ) || 'App name is taken');
		},
	};

	const validatorsForDescription = {
		invalidCharacters: (value) =>
			!/[^A-Za-z0-9-. \n]/.test(trim(value)) ||
			'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces'
	};

	useEffect(() => {
		setProjectName(name);
		setProjectDescription(description);
		setValue('projectName', name);
		setValue('projectDescription', description);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [name, description, editMode]);

	useEffect(() => {
		if (projectDescription) {
			updateProjectDescriptionLength(projectDescription);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectDescription, editMode]);

	useEffect(() => {
		async function getAppNames() {
			if (!b2cConsumerAppID) {
				return;
			}
			try {
				let queryParamList = '?';
				if (b2cConsumerAppID) {
					queryParamList += `withoutb2c=${b2cConsumerAppID}`;
				}
				if (b2bConsumerAppID) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `withoutb2b=${b2bConsumerAppID}`;
				}
				if (associatedProject.b2cConsumerAppID) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `withoutProdb2c=${associatedProject.b2cConsumerAppID}`;
				}
				if (associatedProject.b2bConsumerAppID) {
					queryParamList += (queryParamList.length > 1 ? '&' : '') + `withoutProdb2b=${associatedProject.b2bConsumerAppID}`;
				}
				const appNameResponse = await axios(
					`${import.meta.env.VITE_ROOT_PATH}/api/organization/applications/name${queryParamList}`
				);
				if (appNameResponse && appNameResponse.data) {
					setApplicationNameList(appNameResponse.data);
				}
			} catch (e) {
				const errorMessage = get(e, 'response.data.message',
					'Something went wrong when trying to get application name list. Please try again.'
				);
				dispatch(showErrorModal(errorMessage));
			}
		}
		getAppNames();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [name, b2cConsumerAppID]);

	const updateData = async (newState) => {
		if (newState.projectName === projectNamePrime && newState.projectDescription === projectDescriptionPrime) {
			closeModal();
		} else {
			dispatch(setLoading(true));
			setBusy(true);
			try {
				const { data } = await axios.post(
					`${import.meta.env.VITE_ROOT_PATH}/api/project/name-description`, { projectID, ...newState }
				);
				setProjectDetail((prevState) => {
					return {
						...prevState,
						name: data.name,
						description: data.description
					};
				});
				if (data) {
					handleResponse('Changes were successfully saved', 'success');
				}
			} catch (e) {
				handleResponse(
					'Something went wrong when trying to update project name and description. Please try again.',
					'error'
				);
				const errorMessage = get(e, 'response.data.message',
					'Something went wrong when trying to update project name and description. Please try again.'
				);
				dispatch(showErrorModal(errorMessage));
			}
			setEditMode(false);
			setBusy(false);
			dispatch(setLoading(false));
		}
	};

	const ShowError = ({ message }) => {
		return (
			<SdfAlert slot='alert' size='sm' status='error' type='inline'>
				{message}
			</SdfAlert>
		);
	};

	const closeModal = () => {
		setEditMode(false);
		reset(defaultValues);
		setSaveDisabled(true);
	};

	function ProductionStatus({ status = 'Not Live' }) {
		return capitalize(status.replace('_', ' '));
	}

	const colorByStatus = (status = 'not live') => {
		try {
			if (status.status.replace('_', ' ').toLowerCase()) {
				if (status === 'not live') {
					return 'changed';
				} else if (status === 'live') {
					return 'complete';
				} else if (status === 'suspended') {
					return 'failed';
				} else {
					return 'changed';
				}
			}
		} catch (e) {
			return 'changed';
		}
	};

	const updateProjectDescriptionLength = (value) => {
		const { endLineChar, lengthWithoutEndlineChar } = descriptionLength(value);
		setNumberOfEndLineCharacter(endLineChar);
		setProjectDescriptionLength(lengthWithoutEndlineChar);
	};

	const handleResponse = (message, status = 'error') => {
		setToast({
			key: uniqueId(),
			status: status,
			message: message,
			showToast: true
		});
	};

	return (
		<div>
			<div>
				<div className='flex flew-row items-center'>
					<div className='text-heading-06 font-bold mr-2 max-w-8/12'>
						<CustomToolTip text={projectName} startEllipsisLen='20' width='100%' />
					</div>
					<div className='max-w-1/4'>
						<SdfTooltip attachmentPoint={'bottom'} className={'ml-1'}>
							<SdfIcon
								className='mr-4 relative'
								style={{ top: '0.25rem' }}
								icon='alert-info'
								slot='tooltip-target'
							/>
							{description && <span>{description}</span>}
						</SdfTooltip>
						<SdfLink
							className='relative font-medium'
							style={{ top: '0.25rem' }}
							onClick={() => {
								setEditMode(true);
							}}
						>
							<SdfIcon className='cursor-pointer relative' icon='action-edit' />
							Edit
						</SdfLink>
					</div>
				</div>
				{type === 'DEV' && (
					<div className='mt-1'>
						<SdfBadge status='in-progress'>Development</SdfBadge>
						{legacy && (
							<SdfBadge className='ml-2' status='default'>
								Legacy
							</SdfBadge>
						)}
					</div>
				)}
				{type === 'PROD' && (
					<div className='mt-1'>
						<SdfBadge className='mr-2' status='new'>
							Production
						</SdfBadge>
						<SdfBadge status={colorByStatus(status)}>
							<ProductionStatus status={status} />
						</SdfBadge>
						{legacy && (
							<SdfBadge className='ml-2' status='default'>
								Legacy
							</SdfBadge>
						)}
					</div>
				)}
			</div>

			<SdfFocusPane
				visible={editMode}
				size='sm'
				dismissOnClickOutside={true}
				onSdfDismiss={() => {
					closeModal();
				}}
				closeable={true}
				hideAcceptButton={true}
				hideDismissButton={true}
				heading={'Edit name and description'}
			>
				{busy && (
					<SdfBusyIndicator style={{ color: '#1a8099' }} size='lg' overlay={true} fullScreen={false} />
				)}
				<div className='ml-6 mr-6'>
					<div slot='page-content'>
						<div className=''>
							<div className='pb-4'>
								<Controller
									name='projectName'
									control={control}
									rules={{
										validate: validatorsForName,
									}}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<SdfInput
											label='Project Name'
											name='projectName'
											className='mt-2 w-full'
											onSdfInput={({ detail }) => {
												onChange(detail);
												saveDisabled && setSaveDisabled(false);
											}}
											value={value}
											state={error ? 'error' : 'normal'}
											maxlength={40}
										>
											{error && error.message && (<ShowError message={error.message} />)}
										</SdfInput>
									)}
								/>
							</div>
							<div>
								<Controller
									name='projectDescription'
									control={control}
									rules={{
										validate: validatorsForDescription,
									}}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<SdfTextarea
											name='projectDescription'
											label='Description (256 max char)'
											className='mt-2'
											rows={6}
											onSdfInput={({ detail }) => {
												updateProjectDescriptionLength(detail);
												onChange(detail);
												saveDisabled && setSaveDisabled(false);
											}}
											value={value}
											state={error ? 'error' : 'normal'}
											maxlength={256 + numberOfEndLineCharacter}
											resize={'none'}
										>
											{error && error.message && (<ShowError message={error.message} />)}
										</SdfTextarea>
									)}
								/>
								<div className='mt-2 text-right'>{projectDescriptionLength}/256</div>
							</div>
						</div>
					</div>
				</div>
				<div slot="custom-buttons" className="mt-3">
					<SdfButton onClick={() => closeModal()}>
						Cancel
					</SdfButton>
					<SdfButton
						emphasis="primary"
						onClick={handleSubmit((data) => updateData(data))}
						className="ml-3"
						{...(saveDisabled) && { disabled: true }}
					>
						<div style={{ height: '1.625rem' }}>
							Save
						</div>
					</SdfButton>
				</div>
			</SdfFocusPane>
			{toast && (
				<Toast
					key={get(toast, 'key')}
					status={get(toast, 'status')}
					message={get(toast, 'message')}
					showToast={get(toast, 'showToast')}
				/>
			)}
		</div>
	);
}

export default withContext(ProjectTitle, ProjectContext);
